/**
 * Application Constants
 * 
 * Provides static mappings and utility functions for translating station statuses
 * between English keys and Ukrainian display values.
 */

/**
 * Station Status Mapping
 * 
 * Maps English status keys to Ukrainian display labels for station status representation.
 * Used throughout the application for status display and filtering.
 * 
 * @type {Object.<string, string>}
 * @property {string} active - діюча (active)
 * @property {string} closed - закрита (closed)
 * @property {string} transferred - переведено (transferred)
 * @property {string} external_rent - зовнішня оренда (external rent)
 * @property {string} sold - продано (sold)
 * @property {string} rent - оренда (rent)
 * @property {string} rent_terminated - припинено оренду (rent terminated)
 * @property {string} temporarily_closed - тимчасово закрита (temporarily closed)
 * @property {string} autonomous - автономна (autonomous)
 */
export const STATION_STATUS_MAP: { [key: string]: string } = {
  'active': 'діюча',
  'closed': 'закрита',
  'transferred': 'переведено',
  'external_rent': 'зовнішня оренда',
  'sold': 'продано',
  'rent': 'оренда',
  'rent_terminated': 'припинено оренду',
  'temporarily_closed': 'тимчасово закрита',
  'autonomous': 'автономна',
};

/**
 * Translates English status key to Ukrainian display label
 * 
 * @param {string} status - English status key (e.g., 'active', 'closed')
 * @returns {string} Ukrainian status label or original status if not found in mapping
 * @example
 * translateStatus('active') // Returns: 'діюча'
 * translateStatus('unknown') // Returns: 'unknown'
 */
export const translateStatus = (status: string): string => {
  return STATION_STATUS_MAP[status] || status;
};

/**
 * Reverse translates Ukrainian status label to English key
 * 
 * @param {string} ukrainianStatus - Ukrainian status label (e.g., 'діюча', 'закрита')
 * @returns {string} English status key or original status if not found in mapping
 * @example
 * getStatusKey('діюча') // Returns: 'active'
 * getStatusKey('unknown') // Returns: 'unknown'
 */
export const getStatusKey = (ukrainianStatus: string): string => {
  const entry = Object.entries(STATION_STATUS_MAP).find(([, value]) => value === ukrainianStatus);
  return entry ? entry[0] : ukrainianStatus;
};
